/**
 * AI Consent Modal
 *
 * Apple Guideline 5.1.1(i) / 5.1.2(i) require that any third-party AI usage
 * be explicitly disclosed and user-consented BEFORE data is sent. This modal
 * is shown the first time a user invokes any AI feature in the app (Rules
 * Generate, AI Assistant, CV Board Detection). User's choice is persisted
 * locally so they're not re-prompted on every interaction.
 *
 * Usage:
 *   import { useAiConsent } from '../components/AiConsentModal';
 *   const { ensureConsent, AiConsentModalElement } = useAiConsent();
 *   const onGenerate = async () => {
 *     if (!(await ensureConsent())) return;        // user declined
 *     // safe to call AI endpoint
 *   };
 *   return <>{AiConsentModalElement}<YourUI /></>;
 */
import React, { useCallback, useRef, useState } from 'react';
import axios from 'axios';
import { Robot, ShieldCheck, X } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'cyb_ai_consent';
// Native-app-specific key. On iOS/Android we ignore any web-side `granted`
// value so the modal *always* shows on the first AI tap inside the binary —
// gives Apple's reviewer a clear, in-app proof-of-consent moment.
const STORAGE_KEY_NATIVE = 'cyb_ai_consent_in_app';

function isNativeApp() {
  try { return !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform()); } catch { return false; }
}
function readConsent() {
  try {
    if (isNativeApp()) return localStorage.getItem(STORAGE_KEY_NATIVE) === 'granted';
    return localStorage.getItem(STORAGE_KEY) === 'granted';
  } catch { return false; }
}
function writeConsent(value) {
  try {
    const v = value ? 'granted' : 'denied';
    localStorage.setItem(STORAGE_KEY, v);
    localStorage.setItem(STORAGE_KEY_NATIVE, v);
  } catch { /* ignore */ }
}

export function useAiConsent() {
  const [open, setOpen] = useState(false);
  const resolverRef = useRef(null);

  const ensureConsent = useCallback(() => {
    if (readConsent()) return Promise.resolve(true);
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setOpen(true);
    });
  }, []);

  const handleAllow = () => {
    writeConsent(true);
    setOpen(false);
    // Mirror choice to the backend so it follows the account, not just the device.
    // Fire-and-forget — UX must not block on the network call.
    try {
      axios.post(`${API}/api/users/me/ai-consent`, { granted: true }, { withCredentials: true }).catch(() => {});
    } catch { /* ignore */ }
    if (resolverRef.current) { resolverRef.current(true); resolverRef.current = null; }
  };
  const handleDeny = () => {
    writeConsent(false);
    setOpen(false);
    try {
      axios.post(`${API}/api/users/me/ai-consent`, { granted: false }, { withCredentials: true }).catch(() => {});
    } catch { /* ignore */ }
    if (resolverRef.current) { resolverRef.current(false); resolverRef.current = null; }
  };

  const AiConsentModalElement = open ? (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={handleDeny} />
      <div
        className="relative w-full max-w-md bg-[#0c0c14] border border-white/10 rounded-2xl p-5 shadow-2xl"
        data-testid="ai-consent-modal"
      >
        <button
          onClick={handleDeny}
          className="absolute top-3 right-3 text-[#555] hover:text-white"
          aria-label="Close"
          data-testid="ai-consent-close"
        >
          <X size={18} />
        </button>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#9D4CDD] to-[#00C8D7] flex items-center justify-center">
            <Robot size={20} weight="duotone" className="text-white" />
          </div>
          <div>
            <h3 className="text-white text-sm font-semibold">Use AI features?</h3>
            <p className="text-[#888] text-[11px]">One-time consent for AI-powered help</p>
          </div>
        </div>
        <p className="text-[#bbb] text-[12px] leading-relaxed mb-3">
          CyBoard uses <span className="text-white font-medium">OpenAI (GPT-4o)</span> to power features like
          AI-summarized rules, the in-app assistant, and computer-vision board detection.
        </p>
        <div className="bg-white/[0.03] border border-white/[0.06] rounded-lg p-3 mb-3 space-y-1.5">
          <div className="flex items-start gap-2">
            <ShieldCheck size={13} weight="fill" className="text-[#00C8D7] mt-0.5 flex-shrink-0" />
            <p className="text-[11px] text-[#ccc]">
              <span className="text-white font-medium">What is sent:</span> the text of your question, the
              name of the game you&apos;re asking about, and (for board detection) the photo you upload.
            </p>
          </div>
          <div className="flex items-start gap-2">
            <ShieldCheck size={13} weight="fill" className="text-[#00C8D7] mt-0.5 flex-shrink-0" />
            <p className="text-[11px] text-[#ccc]">
              <span className="text-white font-medium">Who it goes to:</span> OpenAI, processed via the
              Emergent LLM API on our behalf.
            </p>
          </div>
          <div className="flex items-start gap-2">
            <ShieldCheck size={13} weight="fill" className="text-[#00C8D7] mt-0.5 flex-shrink-0" />
            <p className="text-[11px] text-[#ccc]">
              <span className="text-white font-medium">What is NOT sent:</span> your email, password, profile
              data, friends, location, or any other personal account information.
            </p>
          </div>
          <div className="flex items-start gap-2">
            <ShieldCheck size={13} weight="fill" className="text-[#00C8D7] mt-0.5 flex-shrink-0" />
            <p className="text-[11px] text-[#ccc]">
              <span className="text-white font-medium">Retention:</span> OpenAI does not use your queries to
              train models per their API terms. CyBoard caches answers locally to reduce repeat calls.
            </p>
          </div>
        </div>
        <p className="text-[10px] text-[#777] mb-4">
          Full details in our{' '}
          <a href="/privacy" className="underline text-[#00C8D7]" target="_blank" rel="noreferrer">Privacy Policy</a>.
          You can change this choice anytime in Profile → Settings → AI Preferences.
        </p>
        <div className="flex gap-2">
          <button
            onClick={handleDeny}
            className="flex-1 py-2.5 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] text-[#aaa] hover:text-white text-[12px] font-medium transition-colors"
            data-testid="ai-consent-deny"
          >
            Not now
          </button>
          <button
            onClick={handleAllow}
            className="flex-1 py-2.5 rounded-lg bg-gradient-to-br from-[#9D4CDD] to-[#7B2CBF] text-white text-[12px] font-semibold transition-colors"
            data-testid="ai-consent-allow"
          >
            Allow AI features
          </button>
        </div>
      </div>
    </div>
  ) : null;

  return { ensureConsent, AiConsentModalElement, hasConsent: readConsent };
}

/** Allow the Settings page to revoke or re-grant consent. */
export function clearAiConsent() {
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(STORAGE_KEY_NATIVE);
  } catch { /* ignore */ }
}
